import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ShieldCheck, Lock, AlertCircle, X, FlaskConical } from 'lucide-react';
import { toast } from 'sonner';

/**
 * StepUpAuthModal - Reusable re-authentication modal for sensitive actions.
 *
 * Props:
 *   isOpen       - boolean, controls visibility
 *   onSuccess    - (stepUpToken: string) => void, called with short-lived token
 *   onCancel     - () => void, called when user dismisses
 *   actionLabel  - string, describes the protected action (e.g. "delete this user")
 */
export default function StepUpAuthModal({ isOpen, onSuccess, onCancel, actionLabel = 'perform this action' }) {
  const { api } = useAuth();
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [needsMfa, setNeedsMfa] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [checkingMfa, setCheckingMfa] = useState(true);
  const passwordRef = useRef(null);

  // Check if the current user has MFA enabled when modal opens
  useEffect(() => {
    if (!isOpen) return;
    setPassword('');
    setMfaCode('');
    setError('');
    setLoading(false);
    setCheckingMfa(true);
    (async () => {
      try {
        const res = await api.get('/auth/mfa/status');
        setNeedsMfa(res.data.mfa_enabled);
      } catch {
        setNeedsMfa(false);
      }
      setCheckingMfa(false);
      setTimeout(() => passwordRef.current?.focus(), 100);
    })();
  }, [isOpen, api]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setError('');
    setLoading(true);
    try {
      const payload = { password };
      if (needsMfa && mfaCode) payload.mfa_code = mfaCode;
      const res = await api.post('/auth/step-up', payload);
      const token = res.data.step_up_token;
      const expiresIn = res.data.expires_in || 600;
      const isPreview = res.data.is_preview || false;
      // Store in sessionStorage so it persists across component re-renders
      sessionStorage.setItem('step_up_token', token);
      sessionStorage.setItem('step_up_expires', String(Date.now() + expiresIn * 1000));
      if (isPreview) sessionStorage.setItem('step_up_preview', 'true');
      onSuccess(token);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Authentication failed';
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-5" data-testid="step-up-auth-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden max-h-[90vh]">
        {/* Header */}
        <div className="bg-[#004B8D] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 text-white">
            <ShieldCheck className="w-5 h-5" />
            <h3 className="text-base font-semibold">Security Verification</h3>
          </div>
          <button onClick={onCancel} className="text-white/70 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <p className="text-sm text-slate-600">
            To <span className="font-medium text-slate-800">{actionLabel}</span>, please re-enter your credentials.
          </p>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm" data-testid="step-up-error">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          {checkingMfa ? (
            <div className="h-16 bg-slate-50 rounded-lg animate-pulse" />
          ) : (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="step-up-password" className="text-sm">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    ref={passwordRef}
                    id="step-up-password"
                    type="password"
                    placeholder="Enter your password"
                    className="pl-10 h-11"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    data-testid="step-up-password-input"
                  />
                </div>
              </div>

              {needsMfa && (
                <div className="space-y-1.5">
                  <Label htmlFor="step-up-mfa" className="text-sm">Authenticator Code</Label>
                  <Input
                    id="step-up-mfa"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    className="h-11 font-mono text-center text-lg tracking-widest"
                    value={mfaCode}
                    onChange={e => setMfaCode(e.target.value.replace(/\D/g, ''))}
                    data-testid="step-up-mfa-input"
                  />
                </div>
              )}
            </>
          )}

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" className="flex-1" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-[#004B8D] hover:bg-[#003a6d]"
              disabled={loading || checkingMfa || !password || (needsMfa && mfaCode.length < 6)}
              data-testid="step-up-submit-btn"
            >
              {loading ? 'Verifying...' : 'Verify Identity'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * PreviewSecurityBanner — Shows an inline indicator when preview-mode security
 * relaxations are active. Import and place this in any security-sensitive page.
 */
export function PreviewSecurityBanner() {
  const remaining = getStepUpRemainingMinutes();
  const isPreview = isStepUpPreviewMode();
  if (!isPreview) return null;
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800" data-testid="preview-security-banner">
      <FlaskConical className="w-3.5 h-3.5 shrink-0" />
      <span>
        <strong>Preview mode</strong> — Step-up session extended ({remaining} min left). Live deployment uses strict 10-min windows &amp; 5-attempt lockout.
      </span>
    </div>
  );
}

/**
 * Helper: Check if a valid step-up token exists in session.
 * Returns the token string if valid, null otherwise.
 */
export function getValidStepUpToken() {
  const token = sessionStorage.getItem('step_up_token');
  const expires = Number(sessionStorage.getItem('step_up_expires') || 0);
  if (token && expires > Date.now()) return token;
  // Expired or missing — clean up
  clearStepUpToken();
  return null;
}

/**
 * Helper: Check if step-up session is in preview mode with extended validity.
 */
export function isStepUpPreviewMode() {
  return sessionStorage.getItem('step_up_preview') === 'true' && getValidStepUpToken() !== null;
}

/**
 * Helper: Get remaining step-up validity in minutes.
 */
export function getStepUpRemainingMinutes() {
  const expires = Number(sessionStorage.getItem('step_up_expires') || 0);
  if (expires <= Date.now()) return 0;
  return Math.ceil((expires - Date.now()) / 60000);
}

/**
 * Helper: Clear step-up token (call on logout).
 */
export function clearStepUpToken() {
  sessionStorage.removeItem('step_up_token');
  sessionStorage.removeItem('step_up_expires');
  sessionStorage.removeItem('step_up_preview');
}
